import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import appointmentService from '../../../services/appointment.service';
import { confirmAction, showSuccess } from '../../../utils/ecxSwal';
import {
    getAppointmentStatusMeta,
    getPatientInitials,
} from './subparts/appointmentsQuery';
import AppointmentReminderPills from './AppointmentReminderPills';
import AppointmentActionsMenu from './AppointmentActionsMenu';

function AppointmentListItem({ appt, isSelected, onCheckChange, onStatusUpdated }) {
    const [updating, setUpdating] = useState(false);

    const patient = appt.patient || {};
    const doctor = appt.doctor || {};
    const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
    const initials = getPatientInitials(patient);
    const patientName =
        [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim() || 'Patient';

    const handleCheckbox = (event) => onCheckChange(appt.id, event.target.checked);

    const handleConfirmCancel = async (confirmed) => {
        try {
            setUpdating(true);
            await appointmentService.updateAppointmentStatus(appt.clinicId, appt.id, confirmed);
            onStatusUpdated?.();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update appointment status.');
        } finally {
            setUpdating(false);
        }
    };

    const handleSendReminder = async () => {
        const result = await confirmAction({
            title: 'Send reminder?',
            html: `Send a <strong>Second Reminder</strong> to <strong>${patientName}</strong> using their preferred communication channel (email, SMS, or call).`,
            iconClass: 'bi bi-send',
            iconTone: 'primary',
            confirmText: 'Send Reminder',
            onConfirm: async () => {
                setUpdating(true);
                try {
                    return await appointmentService.sendAppointmentReminder(appt.clinicId, appt.id);
                } finally {
                    setUpdating(false);
                }
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        const medium = result.value?.data?.mediumType || result.value?.mediumType;
        const mediumLabel =
            medium === 'EMAIL' ? 'Email' : medium === 'SMS' ? 'SMS' : medium === 'CALL' ? 'Call' : 'Reminder';
        showSuccess({
            title: 'Reminder Queued',
            text: `${mediumLabel} reminder queued for ${patientName}.`,
        });
        onStatusUpdated?.();
    };

    return (
        <tr className={`appointments-row appointments-row--${statusMeta.variant}`}>
            <td className="appointments-row__checkbox">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelected}
                    onChange={handleCheckbox}
                    aria-label={`Select appointment for ${patient.firstName || ''} ${patient.lastName || ''}`.trim()}
                />
            </td>

            <td>
                <div className="appointments-patient-cell">
                    <span className="appointments-patient-cell__avatar" aria-hidden="true">
                        {initials || <i className="bi bi-person-fill" />}
                    </span>
                    <div className="min-width-0">
                        <Link to={`/patient-files/${appt.patientId}`} className="appointments-patient-cell__name">
                            {patient.firstName} {patient.lastName}
                        </Link>
                        {patient.preferredName && (
                            <div className="appointments-patient-cell__preferred">{patient.preferredName}</div>
                        )}
                    </div>
                </div>
            </td>

            <td>
                <div className="appointments-time-cell">
                    <span className="appointments-time-cell__range">
                        {appt.startTime} – {appt.endTime}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-doctor-cell">
                    <span className="appointments-doctor-cell__name">
                        Dr. {doctor.firstName} {doctor.lastName}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-contact-cell">
                    {patient.email ? (
                        <span className="appointments-contact-item" title={patient.email}>
                            <i className="bi bi-envelope" />
                            {patient.email}
                        </span>
                    ) : null}
                    {patient.cellNumber ? (
                        <span className="appointments-contact-item" title={patient.cellNumber}>
                            <i className="bi bi-phone" />
                            {patient.cellNumber}
                        </span>
                    ) : null}
                    {patient.phoneNumber ? (
                        <span className="appointments-contact-item" title={patient.phoneNumber}>
                            <i className="bi bi-telephone" />
                            {patient.phoneNumber}
                        </span>
                    ) : null}
                    {!patient.email && !patient.cellNumber && !patient.phoneNumber ? (
                        <span className="text-muted small">—</span>
                    ) : null}
                </div>
            </td>

            <td className="appointments-row__reminders">
                <AppointmentReminderPills appointmentStatus={appt.appointmentStatus} />
            </td>

            <td>
                <span className={`appointments-status-pill appointments-status-pill--${statusMeta.variant}`}>
                    <i className={`bi ${statusMeta.icon}`} />
                    {statusMeta.label}
                </span>
            </td>

            <td className="text-end">
                <div className="appointments-actions">
                    {appt.isConfirmed === 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleConfirmCancel(true)}
                            disabled={updating}
                            title="Confirm appointment"
                        >
                            <i className="bi bi-check2-circle" />
                        </button>
                    )}

                    <AppointmentActionsMenu
                        appt={appt}
                        updating={updating}
                        onConfirmCancel={handleConfirmCancel}
                        onSendReminder={handleSendReminder}
                    />
                </div>
            </td>
        </tr>
    );
}

export default AppointmentListItem;
